import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Snackbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack,
  ArrowForward,
  Send as SendIcon,
  CheckCircle,
  RadioButtonUnchecked,
  Info as InfoIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../config/api';

// Import form sections
import BasicInfo from './sections/BasicInfo';
import ProblemContext from './sections/ProblemContext';
import NoveltyCommercial from './sections/NoveltyCommercial';
import PriorArt from './sections/PriorArt';
import DetailedDescription from './sections/DetailedDescription';
import Inventors from './sections/Inventors';

const steps = [
  { label: 'Basic Information', component: BasicInfo, name: 'basic_info' },
  { label: 'Problem & Context', component: ProblemContext, name: 'problem_context' },
  { label: 'Novelty & Commercial Value', component: NoveltyCommercial, name: 'novelty_commercial' },
  { label: 'Prior Art', component: PriorArt, name: 'prior_art' },
  { label: 'Detailed Description', component: DetailedDescription, name: 'detailed_description' },
  { label: 'Inventors', component: Inventors, name: 'inventors' },
];

const IDFForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const [sectionData, setSectionData] = useState<any>({});
  const [sectionStatus, setSectionStatus] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [navigatingStep, setNavigatingStep] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [confidentialityAccepted, setConfidentialityAccepted] = useState(false);

  // Fetch form data on mount
  useEffect(() => {
    fetchForm();
  }, [id]);

  // Auto-save every 30 seconds if enabled
  useEffect(() => {
    if (!autoSaveEnabled) return;

    const interval = setInterval(() => {
      if (Object.keys(sectionData).length > 0) {
        saveCurrentSection(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [sectionData, activeStep, autoSaveEnabled]);

  const fetchForm = async () => {
    try {
      const response = await api.get(`/forms/${id}`);
      const form = response.data.form;
      setFormData(form);
      setCompletionPercentage(form.completionPercentage);

      // Process sections
      const sections: any = {};
      const status: any = {};
      form.sections?.forEach((section: any) => {
        sections[section.sectionName] = section.dataJson || {};
        status[section.sectionName] = section.completed;
      });
      setSectionData(sections);
      setSectionStatus(status);
    } catch (error) {
      console.error('Failed to fetch form:', error);
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const saveCurrentSection = async (
    isAutoSave = false,
    stepIndex = activeStep,
    options: { refresh?: boolean } = {}
  ) => {
    const currentSection = steps[stepIndex].name;
    const data = sectionData[currentSection] || {};
    const shouldRefresh = options.refresh ?? !isAutoSave;

    // Use the current completion status from state
    const completed = sectionStatus[currentSection] || false;

    console.log(`Saving section ${currentSection}:`, { data, completed });

    setSaving(true);
    try {
      const response = await api.put(`/forms/${id}/sections/${currentSection}`, {
        data,
        completed,
      });

      setCompletionPercentage(response.data.completionPercentage);

      if (shouldRefresh) {
        // Refetch form when the user explicitly saves; background navigation saves stay lightweight.
        await fetchForm();
      }

      if (!isAutoSave) {
        setSuccess('Section saved successfully');
      }
    } catch (error: any) {
      console.error('Failed to save section:', error);
      console.error('Error response:', error.response);
      console.error('Request URL:', error.config?.url);
      console.error('Request data:', error.config?.data);
      if (!isAutoSave) {
        setError('Failed to save section');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;

    const previousStep = activeStep;
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setNavigatingStep(nextStep);
    void saveCurrentSection(false, previousStep, { refresh: false }).finally(() => {
      setNavigatingStep(null);
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    if (step === activeStep || navigatingStep !== null) return;

    const previousStep = activeStep;
    setActiveStep(step);
    setNavigatingStep(step);
    void saveCurrentSection(true, previousStep, { refresh: false }).finally(() => {
      setNavigatingStep(null);
    });
  };

  const handleSectionChange = (data: any) => {
    const currentSection = steps[activeStep].name;
    setSectionData((prev: any) => ({
      ...prev,
      [currentSection]: data,
    }));
  };

  const handleSectionComplete = (completed: boolean) => {
    const currentSection = steps[activeStep].name;
    setSectionStatus((prev) => ({
      ...prev,
      [currentSection]: completed,
    }));
  };

  // Sample data for testing
  const populateSampleData = async () => {
    const sampleData: any = {
      basic_info: {
        title: 'AI-Powered Patent Analysis System',
        dateOfConception: '15-01-2024',
        field: 'Software / Artificial Intelligence',
        boundByAgreement: 'no',
        agreementDetails: '',
        stageOfDevelopment: 'prototype',
        summary: 'An intelligent system that uses machine learning to analyze patent documents and identify potential infringements, reducing analysis time by 90% while improving accuracy through semantic understanding of patent language.',
      },
      problem_context: {
        problemStatement: 'Current patent analysis is manual, time-consuming, and prone to human error. Legal teams spend hundreds of hours reviewing patent documents, searching for prior art, and evaluating potential infringements.',
        currentSolutions: 'Manual review by patent attorneys, basic keyword search tools, simple database queries, and traditional patent search services. These solutions are slow, expensive, limited to exact keyword matches, miss context and nuances, require extensive manual review, and cannot handle large volumes efficiently.',
      },
      novelty_commercial: {
        innovativeFeatures: 'Novel use of transformer models specifically trained on patent language and legal terminology. Unique semantic search that understands technical context beyond keywords. Automated risk scoring based on claim overlap analysis. Real-time processing of newly filed patents. The system fills a major gap in prior art by providing AI-powered semantic understanding rather than simple keyword matching.',
        keyAdvantages: 'Reduces analysis time by 90%, increases accuracy of infringement detection by 75%, provides automated reporting, scales to handle thousands of patents simultaneously, and costs 80% less than traditional manual review.',
        realWorldApplications: 'Target market includes law firms, patent offices, technology companies with large IP portfolios, and corporate innovation departments. Global legal tech market represents a $17B opportunity. Applications include patent infringement analysis, prior art search, freedom-to-operate studies, patent portfolio management, and litigation support. Reduces analysis costs from $200-500 per patent to $0.15 while cutting turnaround time from weeks to minutes.',
        potentialPartners: 'Major law firms (BigLaw firms), patent offices (USPTO, EPO, JPO), legal tech platforms (LexisNexis, Westlaw), technology giants with large IP portfolios (IBM, Microsoft, Samsung), and university tech transfer offices.',
      },
      prior_art: {
        publiclyDisclosed: 'yes',
        disclosureDetails: 'Presented at the International Patent Conference 2024, San Francisco, USA, March 10-12, 2024. Poster presentation titled "AI-Based Patent Analysis Using Transformer Models".',
        priorArtEntries: [
          {
            id: '1',
            title: 'PatentSight Analytics Platform',
            type: 'product',
            source: 'https://www.patentsight.com',
            differences: 'PatentSight provides basic analytics and visualization but lacks AI-powered semantic search capabilities. Our system uses transformer models trained specifically on patent language, enabling context-aware analysis beyond simple keyword matching.',
          },
          {
            id: '2',
            title: 'US Patent 10,234,567 - Patent Search System',
            type: 'patent',
            source: 'US Patent 10,234,567',
            differences: 'This patent describes a keyword-based patent search system. Unlike this prior art, our invention employs deep learning NLP models that understand semantic relationships and technical context, not just keyword matches.',
          },
          {
            id: '3',
            title: 'Machine Learning in Patent Analysis',
            type: 'paper',
            source: 'Smith et al., Journal of IP Law, 2023, DOI: 10.1234/jip.2023.567',
            differences: 'This research paper discusses general ML applications in patent analysis but focuses on classification tasks. Our invention specifically addresses infringement detection using transformer-based models with automated claim parsing and risk scoring.',
          },
        ],
      },
      detailed_description: {
        technicalDescriptionAndWorkingPrinciple: 'Structure/Architecture:\nThe system comprises three main modules (see Figure 1): (1) Data Ingestion Module 10, (2) NLP Processing Engine 20, and (3) Analysis & Reporting Module 30.\n\nThe Data Ingestion Module 10 includes patent document parser 11, claim extractor 12, and metadata processor 13. It processes patent documents in PDF, XML, and text formats, extracting structured data including claims, descriptions, citations, and figures.\n\nThe NLP Processing Engine 20 utilizes transformer-based models (BERT/GPT architecture) with custom patent vocabulary trained on 50M+ patent documents. Key components include: text embedding generator 21, semantic similarity calculator 22, claim-to-claim comparison engine 23, and context analyzer 24. The models are deployed on a GPU cluster with parallel processing capabilities.\n\nThe Analysis & Reporting Module 30 contains infringement detector 31, risk scoring algorithm 32, prior art discovery engine 33, and automated report generator 34. All modules communicate via REST APIs with Redis caching layer for performance optimization.\n\nOperation/Working Principle:\nStep 1: Patent documents are ingested through parser 11, which converts them into structured JSON format containing claims, descriptions, and metadata.\n\nStep 2: The claim extractor 12 identifies independent and dependent claims using rule-based parsing combined with ML classification, achieving 98% accuracy.\n\nStep 3: Text embedding generator 21 converts patent text into 768-dimensional vectors using fine-tuned BERT model, capturing semantic meaning beyond keywords.\n\nStep 4: Semantic similarity calculator 22 performs cosine similarity search across patent corpus using FAISS vector index, retrieving top-K similar patents (K=100) in under 500ms.\n\nStep 5: Claim-to-claim comparison engine 23 analyzes overlap between claims using attention mechanisms to identify matching concepts, synonyms, and functional equivalents.\n\nStep 6: Risk scoring algorithm 32 calculates infringement probability (0-100%) based on claim overlap percentage, citation patterns, assignee relationships, and historical litigation data. Scoring formula: Risk = 0.4×ClaimOverlap + 0.3×SemanticSimilarity + 0.2×CitationScore + 0.1×LitigationHistory.\n\nStep 7: Results are compiled by report generator 34 with visual highlights, risk heat maps, and actionable recommendations.\n\nControl sequences: System employs feedback loop where false positives/negatives from user feedback retrain the ML models weekly using active learning. Processing queue uses priority scheduling based on patent filing dates and user-defined importance levels.\n\nAlternative Embodiments:\nEmbodiment 1 (Cloud SaaS): Deploy on AWS/GCP with auto-scaling Kubernetes clusters, PostgreSQL database, and S3 object storage. Supports multi-tenant architecture with user isolation.\n\nEmbodiment 2 (On-Premise): Package as Docker containers deployable on enterprise infrastructure. Uses local MySQL/MongoDB database and file system storage. Includes air-gapped deployment option for high-security environments.\n\nEmbodiment 3 (Hybrid): Critical data remains on-premise while compute-intensive NLP processing offloads to cloud. Uses encrypted VPN tunnels for secure data transfer.\n\nEmbodiment 4 (Alternative NLP): Replace BERT with domain-specific "PatentBERT" trained exclusively on patent corpus, or use ensemble approach combining BERT, RoBERTa, ALBERT, and XLNet for improved accuracy (+8% precision).\n\nEmbodiment 5 (Alternative Risk Scoring): Implement Bayesian network model instead of weighted formula, incorporating additional factors like patent family size, opposition history, and inventor track record. Alternatively, use neural network with 5 hidden layers trained on 100K+ litigation outcomes.\n\nEmbodiment 6 (Image Analysis): Add computer vision module for analyzing patent drawings using CNN (ResNet-50) to detect visual similarities in mechanical designs, circuit diagrams, and flowcharts.\n\nSpecific Examples/Prototypes:\nExample 1: Prototype tested on USPTO dataset containing 10M+ utility patents (2010-2024). System processed entire corpus in 48 hours using 8-GPU cluster. Achieved 85% precision and 82% recall in identifying prior art compared to manual review by patent attorneys.\n\nExample 2: Field test with ABC Law Firm analyzing 500 active cases. System detected 15 previously missed prior art references that changed infringement assessment. Average analysis time reduced from 40 hours (manual) to 12 minutes (automated), representing 99.5% time reduction.\n\nExample 3: Pilot deployment at XYZ Tech Corp for portfolio management. Analyzed 2,000 internal patents against 500K competitor patents. Identified 43 potential infringement risks and 28 white space opportunities. ROI calculated at 15:1 within 6 months.\n\nExperimental Data:\n- Processing speed: 2.3 seconds per patent (average)\n- Memory usage: 8GB RAM per analysis thread\n- Accuracy metrics: 85% precision, 82% recall, 0.83 F1-score\n- False positive rate: 12% (vs. 8% for manual review)\n- Cost per analysis: $0.15 (vs. $200-500 manual review)\n- Scalability tested: 100K concurrent users, 500K requests/hour\n- Energy efficiency: 0.02 kWh per patent analysis\n\nDrawings/Diagrams:\nFigure 1 shows the overall system architecture with three main modules (10, 20, 30) and their interconnections. Data flows from left (ingestion) to right (reporting) through processing pipeline.\n\nFigure 2 illustrates the NLP processing flow where patent text (input 101) passes through tokenizer 21a, embedding layer 21b, transformer blocks 21c (12 layers), and output layer 21d to generate semantic vectors (output 102).\n\nFigure 3 depicts the claim comparison algorithm using attention mechanism. Reference numeral 23a shows query claim encoding, 23b shows database claim encoding, 23c shows attention weight calculation, and 23d shows similarity scoring matrix.\n\nFigure 4 presents the user interface dashboard with search bar 40a, results panel 40b showing risk scores with color coding (green <30%, yellow 30-70%, red >70%), detailed comparison view 40c, and export options 40d.\n\nFigure 5 demonstrates alternative deployment configurations: 50a (cloud architecture), 50b (on-premise setup), and 50c (hybrid model) with component mapping showing flexibility in implementation.',
      },
      inventors: {
        inventors: [
          {
            name: 'Dr. Sarah Johnson',
            email: 'sarah.johnson@example.com',
            phone: '+1234567890',
            organization: 'Tech Innovations Inc.',
            contributionPercentage: 60,
            isPrimary: true,
          },
          {
            name: 'Prof. Michael Chen',
            email: 'michael.chen@example.com',
            phone: '+1234567891',
            organization: 'University Research Lab',
            contributionPercentage: 40,
            isPrimary: false,
          },
        ],
      },
    };

    // Update local state
    setSectionData(sampleData);

    // Save all sections to backend
    setSaving(true);
    const saveErrors: string[] = [];

    try {
      console.log('Starting to save all sections...');

      for (const step of steps) {
        const sectionName = step.name;
        const data = (sampleData as any)[sectionName] || {};

        console.log(`Saving section ${sectionName}:`, { hasData: Object.keys(data).length > 0 });

        try {
          await api.put(`/forms/${id}/sections/${sectionName}`, {
            data,
            completed: true,
          });
          console.log(`✓ Successfully saved ${sectionName}`);
        } catch (sectionError: any) {
          const errorMsg = `${sectionName}: ${sectionError.response?.data?.error || sectionError.message}`;
          console.error(`✗ Failed to save ${sectionName}:`, sectionError.response?.data || sectionError);
          saveErrors.push(errorMsg);
        }
      }

      // Fetch updated form to get completion percentage
      await fetchForm();

      if (saveErrors.length > 0) {
        setError(`Failed to save some sections: ${saveErrors.join(', ')}`);
      } else {
        setSuccess('Sample data populated and saved successfully! You can now navigate through all sections.');
      }
    } catch (error: any) {
      console.error('Failed to save sample data:', error);
      setError('Failed to save sample data to server');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await api.post(`/forms/${id}/submit`, {
        confidentialityAccepted,
      });
      setSuccess('Form submitted successfully!');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to submit form');
      if (error.response?.data?.incompleteSections) {
        setError(
          `Please complete the following sections: ${error.response.data.incompleteSections.join(', ')}`
        );
      }
    }
    setSubmitDialog(false);
  };

  const isAllSectionsCompleted = () => {
    return steps.every((step) => sectionStatus[step.name] === true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  const CurrentStepComponent = steps[activeStep].component;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Invention Disclosure Form
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Form Number: {formData.formNumber}
            </Typography>
            <Box>
              <Button
                component="a"
                href="/inventors-guide.pdf"
                download
                startIcon={<DownloadIcon />}
                variant="outlined"
                color="primary"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}
              >
                Inventor's Guide
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={populateSampleData}
              color="secondary"
              size="small"
              sx={{ alignSelf: 'center', minHeight: 32 }}
            >
              Fill Sample Data
            </Button>
            <Button onClick={() => navigate('/dashboard')} startIcon={<ArrowBack />}>
              Back to Dashboard
            </Button>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Overall Progress</Typography>
            <Typography variant="body2">{completionPercentage}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            color={completionPercentage === 100 ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{ cursor: 'pointer' }}
                StepIconComponent={() => (
                  <Box
                    sx={{ cursor: 'pointer' }}
                  >
                    {sectionStatus[step.name] ? (
                      <CheckCircle color="success" />
                    ) : index === activeStep ? (
                      <RadioButtonUnchecked color="primary" />
                    ) : (
                      <RadioButtonUnchecked />
                    )}
                  </Box>
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {navigatingStep !== null && (
          <Box sx={{ mt: -2, mb: 3 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Saving current section...
            </Typography>
          </Box>
        )}

        {/* Form Content */}
        <Box sx={{ minHeight: 400 }}>
          <CurrentStepComponent
            data={sectionData[steps[activeStep].name] || {}}
            onChange={handleSectionChange}
            onComplete={handleSectionComplete}
          />
        </Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Previous
          </Button>

          <Box>
            <Button
              onClick={() => saveCurrentSection()}
              disabled={saving}
              startIcon={<SaveIcon />}
              sx={{ mr: 2 }}
            >
              {saving ? 'Saving...' : 'Save Section'}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setSubmitDialog(true)}
                disabled={!isAllSectionsCompleted()}
                endIcon={<SendIcon />}
              >
                Submit Form
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>

        {/* Auto-save indicator */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
              />
            }
            label="Auto-save enabled (every 30 seconds)"
          />
          {saving && (
            <Typography variant="caption" color="text.secondary">
              Saving...
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Submit Dialog */}
      <Dialog
        open={submitDialog}
        onClose={() => setSubmitDialog(false)}
        disableRestoreFocus
      >
        <DialogTitle>Submit Invention Disclosure</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once submitted, you won't be able to edit this form. Please ensure all information is accurate.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={confidentialityAccepted}
                onChange={(e) => setConfidentialityAccepted(e.target.checked)}
              />
            }
            label="I understand that this invention disclosure contains confidential information and agree to maintain its confidentiality."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!confidentialityAccepted}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          Turnip Innovations Private Limited © 2018-{new Date().getFullYear()}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Made with ❤️ in India.
        </Typography>
      </Box>
    </Container>
  );
};

export default IDFForm;
